import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { categoryLabel } from "@/lib/constants";
import { useNavigate } from "react-router-dom";

export default function Units() {
  const nav = useNavigate();
  const [units, setUnits] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", capacity: 30, gender: "both", location: "" });

  const load = () => api.get("/units").then((r) => setUnits(r.data)).catch(() => setUnits([]));
  useEffect(() => { load(); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    try { await api.post("/units", { ...form, capacity: Number(form.capacity) }); toast.success("تمت الإضافة"); setOpen(false); setForm({ name: "", capacity: 30, gender: "both", location: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/units/${id}`); toast.success("تم الحذف"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (units === null) return <PageLoader />;

  return (
    <>
      <PageHeader title="الوحدات السكنية" subtitle="أماكن إقامة المحاضرات والبرامج"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-unit-btn" className="rounded-xl bg-navy hover:bg-navy-light"><Plus className="me-2 h-4 w-4" /> إضافة وحدة</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading text-navy">إضافة وحدة سكنية</DialogTitle></DialogHeader>
              <div className="grid gap-4 py-2">
                <div><Label>اسم الوحدة</Label><Input data-testid="unit-name" value={form.name} onChange={(e) => set("name")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>السعة</Label><Input type="number" value={form.capacity} onChange={(e) => set("capacity")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>الفئة</Label>
                  <Select value={form.gender} onValueChange={set("gender")}><SelectTrigger className="mt-1.5 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="male">ذكور</SelectItem><SelectItem value="female">إناث</SelectItem><SelectItem value="both">ذكور وإناث</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>الموقع</Label><Input value={form.location} onChange={(e) => set("location")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
              </div>
              <DialogFooter><Button data-testid="unit-save" onClick={submit} disabled={!form.name} className="rounded-xl bg-navy hover:bg-navy-light">حفظ</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      {units.length === 0 ? <EmptyState icon={Building2} title="لا توجد وحدات سكنية" desc="أضف وحدة سكنية لبدء جدولة المحاضرات." /> : (
        <div className="stagger grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {units.map((u) => (
            <div key={u.id} data-testid={`unit-${u.id}`} onClick={() => nav(`/app/units/${u.id}`)} className="card-hover cursor-pointer rounded-2xl border border-slate-100 bg-card p-6 soft-shadow">
              <div className="flex items-start justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy"><Building2 className="h-6 w-6" /></div>
                <Button onClick={(e) => { e.stopPropagation(); remove(u.id); }} data-testid={`del-unit-${u.id}`} size="sm" variant="ghost" className="text-red-400 hover:bg-red-50 hover:text-red-500 h-8 w-8 p-0"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <h3 className="mt-4 font-heading text-lg font-bold text-navy">{u.name}</h3>
              <p className="text-sm text-slate-400">{u.location || "—"}</p>
              <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {u.capacity} مقعد</span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5">{categoryLabel[u.gender]}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
